// /reports display vocabulary: what a dataset, a column, a measure, an enum
// value and a run timestamp are CALLED on screen.
//
// The report engine speaks in registry identifiers (`employee_directory`,
// `department_id`, `sum_net_pay_minor_units`). None of that is for the person
// reading the report, so every name here resolves through the i18n bundle,
// keyed by the identifier. The engine's registry stays the list of what
// EXISTS; this file only decides how each thing reads.
//
// Datasets, columns and enum values are closed domains (the registry and the
// migration CHECKs bound them), so no lookup here passes `defaultValue`: a
// member without a translation must show as its bare key, which the i18n gate
// and any reviewer see, rather than a humanised English word that reads as
// finished copy on a Turkish screen (FRONTEND_PORT_MAP §10 item 3).

import type { TFunction } from "i18next";

import type {
  ColumnValueType,
  DatasetColumn,
  MeasureAggregate,
  ReportDataset,
  ResultColumn,
} from "./api";


export function datasetLabel(t: TFunction, dataset: string): string {
  return t(`reports.dataset_${dataset}`);
}

export function columnLabel(t: TFunction, dataset: string, column: string): string {
  return t(`reports.column_${dataset}_${column}`);
}

/** What count(*) counts, in the dataset's own terms: "Personel sayısı" on the
 *  people dataset rather than a generic "Kayıt sayısı". */
function countLabel(t: TFunction, dataset: string): string {
  return t(`reports.count_${dataset}`);
}

/** A measure's header, built from WHAT it computes. The report's own alias is
 *  deliberately not used: it is an identifier for the exported file
 *  (`personel_sayisi`), and the planner's default alias would put `_minor_units`
 *  on screen. */
export function measureLabel(
  t: TFunction,
  locale: string,
  dataset: string,
  aggregate: MeasureAggregate,
  source: string | null,
  valueType: ColumnValueType | null,
): string {
  if (source === null) return countLabel(t, dataset);
  // Lower-cased so it reads inside the sentence ("Toplam net ücret"); done with
  // the viewer's locale so Turkish dotted/dotless i survives.
  const column = columnLabel(t, dataset, source).toLocaleLowerCase(locale);
  const isDate = valueType === "date";
  if (aggregate === "min") return t(isDate ? "reports.measure_min_date" : "reports.measure_min", { column });
  if (aggregate === "max") return t(isDate ? "reports.measure_max_date" : "reports.measure_max", { column });
  return t(`reports.measure_${aggregate}`, { column });
}

export function resultColumnHeader(
  t: TFunction,
  locale: string,
  dataset: string,
  column: ResultColumn,
): string {
  if (column.aggregate === null) return columnLabel(t, dataset, column.key);
  return measureLabel(t, locale, dataset, column.aggregate, column.source, column.value_type);
}

const ENUM_LABEL_KEY: Readonly<Record<"lifecycle_state" | "employment_type", string>> = {
  lifecycle_state: "people.lifecycle",
  employment_type: "people.employment_type",
};

/** The people module's own labels for the two enum columns, so a status reads
 *  the same here as on the personnel screen. */
export function enumValueLabel(
  t: TFunction,
  kind: "lifecycle_state" | "employment_type",
  value: string,
): string {
  return t(`${ENUM_LABEL_KEY[kind]}.${value}`);
}

/** A UTC instant in the viewer's locale and time zone ("29 Eyl 2026 14:05").
 *  The zone is the caller's effective one (`useTimeZone`), not the browser's, so
 *  the same run reads the same to two people in one company. An unparseable
 *  value comes back as-is; a bad zone name falls back to UTC rather than
 *  throwing out of a render. */
export function formatInstant(locale: string, timeZone: string, iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return iso;
  const options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(value);
  } catch {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(value);
  }
}

export function joinLabels(locale: string, labels: readonly string[]): string {
  return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(labels);
}

/** The columns a row-list report of this dataset shows, as a sentence part. */
export function defaultColumnsLabel(
  t: TFunction,
  locale: string,
  dataset: ReportDataset,
): string {
  return joinLabels(
    locale,
    dataset.default_columns.map((column) => columnLabel(t, dataset.name, column)),
  );
}

/** A saved report's query in plain language, one line per part, so the raw JSON
 *  can stay behind the "advanced" disclosure. Reads only the AST fields the
 *  builder authors; anything else in a stored query is simply not summarised. */
export function describeReportQuery(
  t: TFunction,
  locale: string,
  query: Record<string, unknown>,
  datasets: readonly ReportDataset[],
): readonly string[] {
  const entity = Array.isArray(query["entities"]) ? query["entities"][0] : undefined;
  if (typeof entity !== "string") return [];

  const lines: string[] = [];
  const dataset = datasets.find((d) => d.name === entity);
  const groupings = Array.isArray(query["groupings"])
    ? query["groupings"].filter((g): g is string => typeof g === "string")
    : [];
  const measures = Array.isArray(query["measures"]) ? query["measures"] : [];
  const columnOf = (name: string): DatasetColumn | undefined =>
    dataset?.columns.find((c) => c.name === name);

  lines.push(t("reports.summary_dataset", { dataset: datasetLabel(t, entity) }));

  if (groupings.length === 0 && measures.length === 0) {
    if (dataset !== undefined) {
      lines.push(t("reports.summary_columns", { columns: defaultColumnsLabel(t, locale, dataset) }));
    }
  } else {
    if (groupings.length > 0) {
      lines.push(
        t("reports.summary_groupings", {
          columns: joinLabels(locale, groupings.map((g) => columnLabel(t, entity, g))),
        }),
      );
    }
    const measureLabels: string[] = [];
    for (const raw of measures) {
      if (typeof raw !== "object" || raw === null) continue;
      const m = raw as { field?: unknown; aggregate?: unknown };
      if (typeof m.field !== "string" || typeof m.aggregate !== "string") continue;
      const source = m.field === "*" ? null : m.field;
      measureLabels.push(
        measureLabel(
          t,
          locale,
          entity,
          m.aggregate as MeasureAggregate,
          source,
          source === null ? null : (columnOf(source)?.value_type ?? null),
        ),
      );
    }
    if (measureLabels.length > 0) {
      lines.push(t("reports.summary_measures", { columns: joinLabels(locale, measureLabels) }));
    }
  }

  const filters = Array.isArray(query["filters"]) ? query["filters"] : [];
  if (filters.length > 0) lines.push(t("reports.summary_filters", { count: filters.length }));
  return lines;
}
